import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { assertNotNil } from '../../utils/types';

interface ITabsContext {
  activeIndex: number;
  setActiveIndex: (tabIndex: number) => void;
}

interface ITabsContextProviderProps {
  children: ReactNode;
  defaultActiveIndex?: number;
  onTabChange?: (tabIndex: number) => void;
}

const TabsContext = createContext<ITabsContext | null>(null);

export const TabsContextProvider = ({
  children,
  onTabChange,
}: ITabsContextProviderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleChangeTab = useCallback(
    (tabIndex: number) => {
      setActiveIndex(tabIndex);
      if (onTabChange) {
        onTabChange(tabIndex);
      }
    },
    [onTabChange],
  );

  const value = useMemo(
    () => ({
      activeIndex,
      setActiveIndex: handleChangeTab,
    }),
    [activeIndex, handleChangeTab],
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
};

export const useTabsContext = () => {
  const context = useContext(TabsContext);
  assertNotNil(context);
  return context;
};
